import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
const CustomerMappingPage = () => {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    // Access key management state
    const [showAccessKeyModal, setShowAccessKeyModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customKeyInput, setCustomKeyInput] = useState('');
    const [accessKeyLoading, setAccessKeyLoading] = useState(false);
    const [showAccessKey, setShowAccessKey] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    // Email confirmation modal
    const [showEmailConfirm, setShowEmailConfirm] = useState(false);
    const [emailSending, setEmailSending] = useState(false);
    const [emailResult, setEmailResult] = useState(null);
    const fetchData = async () => {
        try {
            setLoading(true);
            // Fetch customers
            const custResponse = await fetch('/api/customers', {
                credentials: 'include',
            });
            if (custResponse.status === 401) {
                navigate('/login');
                return;
            }
            const custData = await custResponse.json();
            const customersList = custData.data || [];
            // Fetch access key info for each customer
            const customersWithKeys = await Promise.all(customersList.map(async (cust) => {
                try {
                    const keyRes = await fetch(`/api/customers/${cust.id}/access-key`, {
                        credentials: 'include',
                    });
                    if (keyRes.ok) {
                        const keyData = await keyRes.json();
                        if (keyData.data) {
                            return {
                                ...cust,
                                access_key: keyData.data.access_key,
                                custom_key: keyData.data.custom_key,
                                key_is_active: keyData.data.is_active,
                                last_accessed_at: keyData.data.last_accessed_at,
                                access_count: keyData.data.access_count,
                                last_emailed_at: keyData.data.last_emailed_at,
                                key_id: keyData.data.id,
                            };
                        }
                    }
                }
                catch (err) {
                    console.error('Error fetching access key:', err);
                }
                return cust;
            }));
            setCustomers(customersWithKeys);
        }
        catch (error) {
            console.error('Error loading data:', error);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData();
    }, []);
    // Access key management functions
    const handleOpenAccessKeyModal = (customer) => {
        setSelectedCustomer(customer);
        setCustomKeyInput(customer.custom_key || '');
        setShowAccessKey(false);
        setCopySuccess(false);
        setEmailResult(null);
        setShowAccessKeyModal(true);
    };
    const handleGenerateKey = async () => {
        if (!selectedCustomer)
            return;
        setAccessKeyLoading(true);
        try {
            const response = await fetch(`/api/customers/${selectedCustomer.id}/access-key`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to generate key');
            }
            const data = await response.json();
            // Update the customer in state
            setCustomers(prev => prev.map(c => c.id === selectedCustomer.id
                ? {
                    ...c,
                    access_key: data.data.access_key,
                    custom_key: data.data.custom_key,
                    key_is_active: data.data.is_active,
                    last_accessed_at: data.data.last_accessed_at,
                    access_count: data.data.access_count,
                    last_emailed_at: data.data.last_emailed_at,
                    key_id: data.data.id,
                }
                : c));
            setSelectedCustomer(prev => prev ? {
                ...prev,
                access_key: data.data.access_key,
                custom_key: data.data.custom_key,
                key_is_active: data.data.is_active,
                last_accessed_at: data.data.last_accessed_at,
                access_count: data.data.access_count,
                last_emailed_at: data.data.last_emailed_at,
                key_id: data.data.id,
            } : null);
            setShowAccessKey(true);
        }
        catch (error) {
            alert(error.message || 'Failed to generate access key');
        }
        finally {
            setAccessKeyLoading(false);
        }
    };
    const handleSetCustomKey = async () => {
        if (!selectedCustomer || !customKeyInput)
            return;
        setAccessKeyLoading(true);
        try {
            const response = await fetch(`/api/customers/${selectedCustomer.id}/access-key`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ action: 'setCustomKey', customKey: customKeyInput }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to set custom key');
            }
            const data = await response.json();
            // Update the customer in state
            setCustomers(prev => prev.map(c => c.id === selectedCustomer.id
                ? { ...c, custom_key: data.data.custom_key }
                : c));
            setSelectedCustomer(prev => prev ? { ...prev, custom_key: data.data.custom_key } : null);
            alert('Custom key set successfully!');
        }
        catch (error) {
            alert(error.message || 'Failed to set custom key');
        }
        finally {
            setAccessKeyLoading(false);
        }
    };
    const handleToggleKeyStatus = async () => {
        if (!selectedCustomer)
            return;
        setAccessKeyLoading(true);
        try {
            const response = await fetch(`/api/customers/${selectedCustomer.id}/access-key`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ action: 'toggleStatus' }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to toggle key status');
            }
            const data = await response.json();
            // Update the customer in state
            setCustomers(prev => prev.map(c => c.id === selectedCustomer.id
                ? { ...c, key_is_active: data.data.is_active }
                : c));
            setSelectedCustomer(prev => prev ? { ...prev, key_is_active: data.data.is_active } : null);
        }
        catch (error) {
            alert(error.message || 'Failed to toggle key status');
        }
        finally {
            setAccessKeyLoading(false);
        }
    };
    const getPortalUrl = (key) => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/customer-portal/${key}`;
    };
    const handleCopyLink = async () => {
        if (!selectedCustomer?.access_key && !selectedCustomer?.custom_key)
            return;
        const key = selectedCustomer.custom_key || selectedCustomer.access_key;
        const url = getPortalUrl(key);
        try {
            await navigator.clipboard.writeText(url);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
        catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };
    const handleEmailClick = () => {
        if (!selectedCustomer?.email) {
            alert('This customer does not have an email address');
            return;
        }
        setShowEmailConfirm(true);
    };
    const handleSendEmail = async () => {
        if (!selectedCustomer)
            return;
        setEmailSending(true);
        setEmailResult(null);
        try {
            const response = await fetch(`/api/customers/${selectedCustomer.id}/email-access-link`, {
                method: 'POST',
                credentials: 'include',
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to send email');
            }
            setEmailResult({ success: true, message: data.message || `Email sent to ${selectedCustomer.email}` });
            // Update last emailed timestamp
            const now = Math.floor(Date.now() / 1000);
            setCustomers(prev => prev.map(c => c.id === selectedCustomer.id
                ? { ...c, last_emailed_at: now }
                : c));
            setSelectedCustomer(prev => prev ? { ...prev, last_emailed_at: now } : null);
        }
        catch (error) {
            setEmailResult({ success: false, message: error.message || 'Failed to send email' });
        }
        finally {
            setEmailSending(false);
            setShowEmailConfirm(false);
        }
    };
    const formatDate = (timestamp) => {
        if (!timestamp)
            return 'Never';
        return new Date(timestamp * 1000).toLocaleString();
    };
    if (loading) {
        return (_jsx("div", { className: "min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8", children: _jsx("div", { className: "max-w-7xl mx-auto", children: _jsx("div", { className: "flex justify-center items-center h-64", children: _jsx("div", { className: "text-secondary-600", children: "Loading..." }) }) }) }));
    }
    return (_jsxs("div", { className: "min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8", children: [_jsxs("div", { className: "max-w-7xl mx-auto", children: [_jsxs("div", { className: "mb-8", children: [_jsx(Link, { to: "/dashboard", className: "text-primary-600 hover:text-primary-700 mb-4 inline-block", children: "\u2190 Back to Dashboard" }), _jsx("h1", { className: "text-3xl font-bold text-secondary-900", children: "Customer Portal Access" }), _jsx("p", { className: "text-secondary-600 mt-2", children: "Manage secure portal access for customers to view their pricing" })] }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", children: customers.map((customer) => (_jsxs("div", { className: "card bg-white", children: [_jsxs("div", { className: "flex justify-between items-start mb-3", children: [_jsxs("div", { className: "flex-1", children: [_jsx("h3", { className: "font-bold text-secondary-900", children: customer.name }), customer.account_number && (_jsxs("p", { className: "text-xs text-secondary-500", children: ["#", customer.account_number] }))] }), customer.access_key && (_jsx("span", { className: `px-2 py-1 rounded-full text-xs font-medium ${customer.key_is_active
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-red-100 text-red-700'}`, children: customer.key_is_active ? 'Active' : 'Disabled' }))] }), customer.email && (_jsxs("p", { className: "text-sm text-secondary-600 mb-2", children: ["\uD83D\uDCE7 ", customer.email] })), customer.access_key && (_jsxs("div", { className: "text-xs text-secondary-500 mb-3 space-y-1", children: [_jsxs("p", { children: ["Last accessed: ", formatDate(customer.last_accessed_at)] }), _jsxs("p", { children: ["Access count: ", customer.access_count || 0] }), customer.last_emailed_at && (_jsxs("p", { children: ["Last emailed: ", formatDate(customer.last_emailed_at)] }))] })), _jsx("button", { onClick: () => handleOpenAccessKeyModal(customer), className: `w-full py-2 rounded-lg text-sm font-medium transition-colors ${customer.access_key
                                        ? customer.key_is_active
                                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                                        : 'bg-primary-100 text-primary-700 hover:bg-primary-200'}`, children: customer.access_key
                                        ? '🔑 Manage Portal Access'
                                        : '+ Generate Portal Access' })] }, customer.id))) }), customers.length === 0 && (_jsxs("div", { className: "text-center py-12", children: [_jsx("p", { className: "text-secondary-600", children: "No customers found. Add customers first." }), _jsx(Link, { to: "/customers", className: "text-primary-600 hover:text-primary-700 mt-2 inline-block", children: "Go to Customers \u2192" })] }))] }), showAccessKeyModal && selectedCustomer && (_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50", children: _jsxs("div", { className: "bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto", children: [_jsxs("div", { className: "flex justify-between items-start mb-4", children: [_jsxs("div", { children: [_jsxs("h3", { className: "text-lg font-bold text-secondary-900", children: ["Portal Access for ", selectedCustomer.name] }), selectedCustomer.account_number && (_jsxs("p", { className: "text-sm text-secondary-500", children: ["#", selectedCustomer.account_number] }))] }), _jsx("button", { onClick: () => setShowAccessKeyModal(false), className: "text-secondary-400 hover:text-secondary-600", children: "\u2715" })] }), emailResult && (_jsx("div", { className: `mb-4 p-3 rounded-lg ${emailResult.success
                                ? 'bg-green-50 border border-green-200 text-green-700'
                                : 'bg-red-50 border border-red-200 text-red-700'}`, children: emailResult.message })), selectedCustomer.access_key ? (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("span", { className: `px-3 py-1 rounded-full text-sm font-medium ${selectedCustomer.key_is_active
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-red-100 text-red-700'}`, children: selectedCustomer.key_is_active ? '✓ Active' : '✕ Disabled' }), _jsx("button", { onClick: handleToggleKeyStatus, disabled: accessKeyLoading, className: "text-sm text-primary-600 hover:text-primary-700", children: selectedCustomer.key_is_active ? 'Disable' : 'Enable' })] }), _jsxs("div", { className: "bg-secondary-50 p-4 rounded-lg", children: [_jsxs("div", { className: "flex justify-between items-center mb-2", children: [_jsx("span", { className: "text-sm font-medium text-secondary-700", children: "Access Key" }), _jsx("button", { onClick: () => setShowAccessKey(!showAccessKey), className: "text-xs text-primary-600 hover:text-primary-700", children: showAccessKey ? 'Hide' : 'Show' })] }), _jsx("code", { className: "text-sm font-mono text-secondary-900 break-all", children: showAccessKey
                                                ? selectedCustomer.access_key
                                                : '••••••••••••••••••••••••••••••••' })] }), selectedCustomer.custom_key && (_jsxs("div", { className: "bg-secondary-50 p-4 rounded-lg", children: [_jsx("span", { className: "text-sm font-medium text-secondary-700 block mb-2", children: "Custom Key" }), _jsx("code", { className: "text-sm font-mono text-secondary-900", children: selectedCustomer.custom_key })] })), _jsxs("div", { className: "bg-primary-50 p-4 rounded-lg", children: [_jsx("span", { className: "text-sm font-medium text-secondary-700 block mb-2", children: "Portal Link" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "text-xs font-mono text-primary-700 break-all flex-1", children: getPortalUrl(selectedCustomer.custom_key || selectedCustomer.access_key) }), _jsx("button", { onClick: handleCopyLink, className: `px-3 py-1 rounded text-sm font-medium transition-colors ${copySuccess
                                                        ? 'bg-green-500 text-white'
                                                        : 'bg-primary-600 text-white hover:bg-primary-700'}`, children: copySuccess ? '✓ Copied!' : 'Copy' })] })] }), selectedCustomer.email && (_jsxs("button", { onClick: handleEmailClick, disabled: accessKeyLoading || !selectedCustomer.key_is_active, className: "w-full py-3 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed", children: [_jsx("svg", { className: "w-5 h-5", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" }) }), "Email Secure URL to Customer"] })), !selectedCustomer.email && (_jsx("p", { className: "text-sm text-amber-600 bg-amber-50 p-3 rounded-lg", children: "\u26A0\uFE0F This customer does not have an email address. Add one to enable email functionality." })), _jsxs("div", { className: "grid grid-cols-2 gap-4 text-sm", children: [_jsxs("div", { children: [_jsx("span", { className: "text-secondary-500", children: "Last Accessed:" }), _jsx("p", { className: "text-secondary-900", children: formatDate(selectedCustomer.last_accessed_at) })] }), _jsxs("div", { children: [_jsx("span", { className: "text-secondary-500", children: "Total Accesses:" }), _jsx("p", { className: "text-secondary-900", children: selectedCustomer.access_count || 0 })] }), selectedCustomer.last_emailed_at && (_jsxs("div", { className: "col-span-2", children: [_jsx("span", { className: "text-secondary-500", children: "Last Emailed:" }), _jsx("p", { className: "text-secondary-900", children: formatDate(selectedCustomer.last_emailed_at) })] }))] }), _jsxs("div", { className: "border-t border-secondary-200 pt-4", children: [_jsx("label", { className: "block text-sm font-medium text-secondary-700 mb-2", children: "Set Custom Key (Optional)" }), _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { type: "text", value: customKeyInput, onChange: (e) => setCustomKeyInput(e.target.value), placeholder: "e.g., acme-corp-2024", className: "input flex-1" }), _jsx("button", { onClick: handleSetCustomKey, disabled: accessKeyLoading || !customKeyInput, className: "btn btn-outline", children: "Set" })] }), _jsx("p", { className: "text-xs text-secondary-500 mt-1", children: "8-64 characters, letters, numbers, underscores, hyphens only" })] }), _jsx("div", { className: "border-t border-secondary-200 pt-4", children: _jsx("button", { onClick: handleGenerateKey, disabled: accessKeyLoading, className: "text-sm text-amber-600 hover:text-amber-700", children: "\u26A0\uFE0F Regenerate Access Key (invalidates current key)" }) })] })) : (
                        /* No Key Yet */
                        _jsxs("div", { className: "text-center py-6", children: [_jsx("p", { className: "text-secondary-600 mb-4", children: "No portal access key has been generated for this customer yet." }), _jsx("button", { onClick: handleGenerateKey, disabled: accessKeyLoading, className: "btn btn-primary", children: accessKeyLoading ? 'Generating...' : 'Generate Access Key' })] })), _jsx("div", { className: "mt-6 flex justify-end", children: _jsx("button", { onClick: () => setShowAccessKeyModal(false), className: "btn btn-outline", children: "Close" }) })] }) })), showEmailConfirm && selectedCustomer && (_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]", children: _jsxs("div", { className: "bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4", children: [_jsx("h3", { className: "text-lg font-bold text-secondary-900 mb-4", children: "Confirm Email" }), _jsx("p", { className: "text-secondary-600 mb-4", children: "Are you sure you want to send the secure pricing portal URL to:" }), _jsxs("p", { className: "font-medium text-secondary-900 mb-6 p-3 bg-secondary-50 rounded-lg", children: ["\uD83D\uDCE7 ", selectedCustomer.email] }), _jsxs("div", { className: "flex justify-end gap-3", children: [_jsx("button", { onClick: () => setShowEmailConfirm(false), disabled: emailSending, className: "btn btn-outline", children: "Cancel" }), _jsx("button", { onClick: handleSendEmail, disabled: emailSending, className: "btn btn-primary flex items-center gap-2", children: emailSending ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" }), "Sending..."] })) : (_jsx(_Fragment, { children: "Send Email" })) })] })] }) }))] }));
};
export default CustomerMappingPage;
